import {
  Transaction,
  getDocs,
  orderBy,
  query,
  runTransaction,
  setDoc,
} from "firebase/firestore/lite";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import { Dish, FirestoreDishDocument } from "@dashboard/types/dish";

import { readDish, writeDish } from "./firestore-converter/dish";

export interface DishApi {
  fetchDishes(restaurantUuid: string): Promise<Dish[]>;
  setDish(dish: Dish, isNew: boolean): Promise<void>;
  deleteDish(restaurantUuid: string, dishId: string): Promise<void>;
}

/**
 * An implementation of the DishApi that uses Firestore as the backend.
 */
export class FirestoreBackedDishApi implements DishApi {
  private firestoreRefs: FirestoreRefs;
  private beforeDeleteDishFromMenuSection: (
    transaction: Transaction,
    restaurantUuid: string,
    dishId: string
  ) => Promise<void>;

  constructor(params: {
    firestoreRefs: FirestoreRefs;
    beforeDeleteDishFromMenuSection: (
      transaction: Transaction,
      restaurantUuid: string,
      dishId: string
    ) => Promise<void>;
  }) {
    this.firestoreRefs = params.firestoreRefs;
    this.beforeDeleteDishFromMenuSection =
      params.beforeDeleteDishFromMenuSection;
  }

  public fetchDishes(restaurantUuid: string): Promise<Dish[]> {
    return getDocs(
      query(
        this.firestoreRefs.dishes(restaurantUuid),
        // createdAt is always set (for all dishes), and is used to order the dishes.
        orderBy("createdAt", "desc")
      )
    ).then((querySnapshot) =>
      querySnapshot.docs
        .map((doc) => doc.data() as FirestoreDishDocument)
        .map(readDish)
    );
  }

  public setDish(dish: Dish, isNew: boolean): Promise<void> {
    const firestoreDish = writeDish(this.firestoreRefs, dish, isNew);
    return setDoc(
      this.firestoreRefs.dish(dish.restaurantUuid, dish.id),
      firestoreDish,
      {
        merge: true,
      }
    );
  }

  // This would be moved to an API call and handled in the backend later on
  public deleteDish(restaurantUuid: string, dishId: string): Promise<void> {
    return runTransaction(this.firestoreRefs.firestore, async (transaction) => {
      await this.beforeDeleteDishFromMenuSection(
        transaction,
        restaurantUuid,
        dishId
      );
      // deleting the dish itself is not managed by the previous function
      await transaction.delete(this.firestoreRefs.dish(restaurantUuid, dishId));
    });
  }
}
